import { createTheme, MantineColorsTuple } from '@mantine/core';

// Healthcare-appropriate color palettes
const healthyGreen: MantineColorsTuple = [
  '#e6f7f1', // 0 - lightest (backgrounds)
  '#c8ede0', // 1
  '#a3dcc9', // 2
  '#7dcbb3', // 3
  '#5aba9d', // 4
  '#4fa98e', // 5
  '#4a8c71', // 6 - primary shade (default)
  '#3f7961', // 7
  '#356651', // 8
  '#2b5341', // 9 - darkest
];

const accentGreen: MantineColorsTuple = [
  '#e6fcf5', // 0 - lightest
  '#c3fae8', // 1
  '#96f2d7', // 2
  '#63e6be', // 3
  '#38d9a9', // 4
  '#20c997', // 5
  '#12b886', // 6 - primary shade
  '#0ca678', // 7
  '#099268', // 8
  '#087f5b', // 9 - darkest
];

const neutral: MantineColorsTuple = [
  '#f8f9fa', // 0 - lightest
  '#f1f3f5', // 1
  '#e9ecef', // 2
  '#dee2e6', // 3
  '#ced4da', // 4
  '#adb5bd', // 5
  '#868e96', // 6
  '#495057', // 7
  '#343a40', // 8
  '#212529', // 9 - darkest
];

export const theme = createTheme({
  /** Primary color for healthcare (healing green for trust) */
  primaryColor: 'healthyGreen',
  primaryShade: 6,

  /** Custom color palettes */
  colors: {
    healthyGreen,
    accentGreen,
    neutral,
  },

  /** Typography - Readable and professional */
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontFamilyMonospace:
    'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',

  headings: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontWeight: '600',
    sizes: {
      h1: { fontSize: '2.5rem', lineHeight: '1.2' },
      h2: { fontSize: '2rem', lineHeight: '1.3' },
      h3: { fontSize: '1.5rem', lineHeight: '1.4' },
      h4: { fontSize: '1.25rem', lineHeight: '1.45' },
      h5: { fontSize: '1rem', lineHeight: '1.5' },
      h6: { fontSize: '0.875rem', lineHeight: '1.5' },
    },
  },

  /** Spacing - Consistent and generous (healthcare needs breathing room) */
  spacing: {
    xs: '0.5rem', // 8px
    sm: '0.75rem', // 12px
    md: '1rem', // 16px
    lg: '1.5rem', // 24px
    xl: '2rem', // 32px
  },

  /** Border radius - Soft and friendly */
  radius: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '0.75rem',
    lg: '1rem',
    xl: '1.5rem',
  },
  defaultRadius: 'md',

  /** Shadows - Subtle elevation */
  shadows: {
    xs: '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1)',
    sm: '0 1px 3px rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0px 10px 15px -5px, rgba(0, 0, 0, 0.04) 0px 7px 7px -5px',
    md: '0 1px 3px rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0px 20px 25px -5px, rgba(0, 0, 0, 0.04) 0px 10px 10px -5px',
    lg: '0 1px 3px rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0px 28px 23px -7px, rgba(0, 0, 0, 0.04) 0px 12px 12px -7px',
    xl: '0 1px 3px rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0px 36px 28px -7px, rgba(0, 0, 0, 0.04) 0px 17px 17px -7px',
  },

  /** Focus ring - High visibility for keyboard navigation */
  focusRing: 'auto',

  /** Auto contrast - Better text readability on colored backgrounds */
  autoContrast: true,
  luminanceThreshold: 0.3,

  /** Responsive breakpoints - Mobile-first */
  breakpoints: {
    xs: '36em', // 576px
    sm: '48em', // 768px
    md: '62em', // 992px
    lg: '75em', // 1200px
    xl: '88em', // 1408px
  },

  /** Component-specific overrides */
  components: {
    Button: {
      defaultProps: {
        radius: 'md',
      },
      styles: {
        root: {
          fontWeight: 500,
          transition: 'all 0.2s ease',
        },
      },
    },
    Card: {
      defaultProps: {
        radius: 'md',
        shadow: 'sm',
        padding: 'lg',
      },
    },
    Paper: {
      defaultProps: {
        radius: 'md',
      },
    },
  },
});
